import axios from 'axios';

// API Base URLs Configuration
const getApiBaseUrls = () => {
  const origin = window.location.origin;

  // Origin'de 'local' veya 'test' kelimesi var mı kontrol et
  const isLocalOrTest = origin.toLowerCase().includes('local') ||
    origin.toLowerCase().includes('test');

  console.log('Current Origin:', origin);
  console.log('Is Local/Test Environment:', isLocalOrTest);

  if (isLocalOrTest) {
    // Test/Development ortamında sabit URL'ler kullan
    return {
      PRO: window.location.origin + '/api',
      NORDEX: window.location.origin + '/api',
    };
  } else {
    // Production ortamında window.location.origin kullan
    return {
      PRO: `${origin}/api`,
      NORDEX: `${origin}/api`,
    };
  }
};

const API_BASE_URLS = getApiBaseUrls();

// Default axios instance (PRO)
const defaultClient = axios.create({
  baseURL: API_BASE_URLS.PRO,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Nordex axios instance
const nordexClient = axios.create({
  baseURL: API_BASE_URLS.NORDEX,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - token ekle (her iki client için)
const addTokenInterceptor = (client) => {
  client.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem('auth:token');
      // Eğer zaten authorization header'ı varsa, interceptor override etmesin
      if (token && !config.headers.authorization) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor - error handling
  client.interceptors.response.use(
    (response) => {
      return response;
    },
    (error) => {
      if (error.response?.status === 401) {
        // Token expired or invalid
        localStorage.removeItem('auth:token');
        localStorage.removeItem('auth:user');
        window.location.href = '/login';
      }
      return Promise.reject(error);
    }
  );
};

// Interceptors'ları ekle
addTokenInterceptor(defaultClient);
addTokenInterceptor(nordexClient);

// API Service Class
class ApiService {
  // Generic request method with client selection
  async request(method, endpoint, data = null, config = {}, client = 'default') {
    try {
      const selectedClient = client === 'nordex' ? nordexClient : defaultClient;
      const response = await selectedClient.request({
        method,
        url: endpoint,
        data,
        ...config,
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // HTTP Methods with client selection
  async get(endpoint, config = {}, client = 'default') {
    return this.request('GET', endpoint, null, config, client);
  }

  async post(endpoint, data, config = {}, client = 'default') {
    return this.request('POST', endpoint, data, config, client);
  }

  async put(endpoint, data, config = {}, client = 'default') {
    return this.request('PUT', endpoint, data, config, client);
  }

  async patch(endpoint, data, config = {}, client = 'default') {
    return this.request('PATCH', endpoint, data, config, client);
  }

  async delete(endpoint, config = {}, client = 'default') {
    return this.request('DELETE', endpoint, null, config, client);
  }

  // File upload
  async upload(endpoint, file, config = {}) {
    const formData = new FormData();
    formData.append('file', file);

    return this.request('POST', endpoint, formData, {
      ...config,
      headers: {
        'Content-Type': 'multipart/form-data',
        ...config.headers,
      },
    });
  }

  // ===== AUTHENTICATION METHODS =====

  // Register user
  async registerUser(userData) {
    return this.post('/registration/client/Registration/RegisterUser', userData);
  }

  // Login user
  async loginUser(credentials) {
    const response = await this.post('/authentication/Auth/Login', credentials);


    // 2FA kontrolü
    if (response && response.data && response.data.isTwoFactorEnabled === true) {
      // 2FA aktif ise sadece geçici token kaydet
      localStorage.setItem('temp:token', response.data.token);
      // 2FA login sayfasına yönlendir
      window.location.href = '/two-factor-login';
      return response;
    }

    // 2FA kapalı ise normal login flow
    if (response && response.data && response.data.isTwoFactorEnabled === false) {
      // Normal token kaydet
      if (response.data.token) {
        localStorage.setItem('auth:token', response.data.token);
      }
      // Kullanıcıyı giriş yapmış olarak işaretle
      localStorage.setItem('auth:user', JSON.stringify({ isAuthenticated: true, needs2FA: false }));
    }

    return response;
  }

  async getUserInfo() {
    const token = localStorage.getItem('auth:token');
    const currentOrigin = window.location.origin;
    const isLocalOrTest = currentOrigin.toLowerCase().includes('local') ||
      currentOrigin.toLowerCase().includes('test');
    try {
      // GetUserInfo için özel URL kullan
      const origin = isLocalOrTest ? currentOrigin : currentOrigin;
      const response = await axios.get(`${origin}/api/authentication/Auth/GetMe`, {
        headers: {
          'authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        timeout: 10000,
      });
      return response;
    } catch (error) {
      // 400 bad request ve "Token expired!!!" mesajı kontrolü
      if (error.response?.status === 400 && 
          error.response?.data?.Message === "Token expired!!!") {
        // Token süresi dolmuş, kullanıcıyı logout yap
        localStorage.removeItem('auth:token');
        localStorage.removeItem('auth:user');
        window.location.href = '/login';
        return;
      }
      // Diğer hatalar için normal error handling
      throw error;
    }
  }

  async getCryptoPaymentMethods() {
    const token = localStorage.getItem('auth:token');
    const currentOrigin = window.location.origin;
    const isLocalOrTest = currentOrigin.toLowerCase().includes('local') ||
      currentOrigin.toLowerCase().includes('test');
    const origin = isLocalOrTest ? currentOrigin : currentOrigin;
    const response = await axios.get(`${origin}/api/treasury/Treasury/GetCryptoPaymentMethods`, {
      headers: {
        'authorization': `Bearer ${token}`,
        'Accept': 'application/json',
      },
      timeout: 10000,
    });
    return response;
  }

  async getBankPaymentMethods() {
    const token = localStorage.getItem('auth:token');
    const currentOrigin = window.location.origin;
    const isLocalOrTest = currentOrigin.toLowerCase().includes('local') ||
      currentOrigin.toLowerCase().includes('test');
    const origin = isLocalOrTest ? currentOrigin : currentOrigin;
    const response = await axios.get(`${origin}/api/treasury/Treasury/GetBankPaymentMethods`, {
      headers: {
        'authorization': `Bearer ${token}`,
        'Accept': 'application/json',
      },
      timeout: 10000,
    });
    return response;
  }

  async getCompanyInfo() {
    const currentOrigin = window.location.origin;
    const isLocalOrTest = currentOrigin.toLowerCase().includes('local') ||
      currentOrigin.toLowerCase().includes('test');

    // Local veya test ortamında statik URL, production'da dinamik URL
    const origin = isLocalOrTest ? currentOrigin : currentOrigin;

    const response = await axios.get(`${origin}/api/Global/GetCompanyInfo`, {
      headers: {
        'Accept': 'application/json, text/plain, */*',
        'Content-Type': 'application/json',
        'brand': 'nordex'
      },
      timeout: 10000,
    });
    return response;
  }

  async getReferenceLink() {
    const token = localStorage.getItem('auth:token');
    return this.get('/references/client/references/GetReferenceLink', {
      headers: {
        'authorization': `Bearer ${token}`
      }
    }, 'default');
  }

  async changePassword(oldPassword, newPassword) {
    const token = localStorage.getItem('auth:token');
    return this.post('/authentication/Auth/ChangePassword', {
      oldPassword,
      newPassword
    }, {
      headers: {
        'authorization': `Bearer ${token}`
      }
    }, 'default');
  }

  async get2FactorStatus() {
    return this.get('/authentication/Auth/Get2FactorStatus', {}, 'pro');
  }

  async generateQrCode() {
    return this.post('/authentication/Auth/GenerateQrCode', {}, 'pro');
  }

  async validatePin(pin) {
    return this.post('/authentication/Auth/ValidatePin', JSON.stringify(pin), 'pro');
  }

  async deactivate2Factor() {
    return this.post('/authentication/Auth/Deactivate2Factor', {}, 'pro');
  }

  async verifyTwoFactor(twoFactorPin) {
    const tempToken = localStorage.getItem('temp:token');
    const config = {
      headers: {
        'Authorization': `Bearer ${tempToken}`
      }
    };
    return this.post(`/authentication/Auth/VerifyTwoFactor?twoFactorPin=${twoFactorPin}`, {}, config, 'pro');
  }

  async validateBackupCode(backupCode) {
    const tempToken = localStorage.getItem('temp:token');
    const config = {
      headers: {
        'Authorization': `Bearer ${tempToken}`
      }
    };
    return this.post('/authentication/Auth/ValidateBackupCode', JSON.stringify(backupCode), config, 'pro');
  }

  // ===== TRANSACTION METHODS =====

  // Get crypto withdrawals
  async getCryptoWithdrawals() {
    const token = localStorage.getItem('auth:token');
    return this.get('/transactions/client/Transactions/GetCryptoWithdrawals', {
      headers: {
        'Authorization': `Bearer ${token}`,
        'authorization': `Bearer ${token}` // Interceptor'ı bypass etmek için
      }
    });
  }

  // Get bank withdrawals
  async getBankWithdrawals() {
    const token = localStorage.getItem('auth:token');
    return this.get('/transactions/client/Transactions/GetFiatWithdrawals', {
      headers: {
        'Authorization': `Bearer ${token}`,
        'authorization': `Bearer ${token}` // Interceptor'ı bypass etmek için
      }
    });
  }

  // Get open positions
  async getOpenPositions() {
    const token = localStorage.getItem('auth:token');
    return this.get('/transactions/client/Transactions/GetOpenPositionsForUser', {
      headers: {
        'Authorization': `Bearer ${token}`,
        'authorization': `Bearer ${token}` // Interceptor'ı bypass etmek için
      }
    });
  }

  // Get closed positions
  async getClosedPositions(startTime = '', endTime = '') {
    const token = localStorage.getItem('auth:token');
    const body = {
      StartTime: startTime || new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      EndTime: endTime || new Date().toISOString().split('T')[0]
    };

    return this.post('/transactions/client/Transactions/GetClosedPositionsForUser', body, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'authorization': `Bearer ${token}` // Interceptor'ı bypass etmek için
      }
    });
  }

  // Get deposits (Yatırım Geçmişi)
  async getDeposits(startTime = '', endTime = '') {
    const token = localStorage.getItem('auth:token');
    const body = {
      StartTime: startTime || new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      EndTime: endTime || new Date().toISOString().split('T')[0]
    };

    return this.post('/transactions/client/Transactions/GetDepositsForUser', body, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'authorization': `Bearer ${token}` // Interceptor'ı bypass etmek için
      }
    });
  }

  // Create crypto withdrawal
  async createCryptoWithdrawal(withdrawalData) {
    const token = localStorage.getItem('auth:token');
    return this.post('/transactions/client/Transactions/CreateCryptoWithdrawal', withdrawalData, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'authorization': `Bearer ${token}` // Interceptor'ı bypass etmek için
      }
    });
  }

  // Create fiat withdrawal
  async createFiatWithdrawal(withdrawalData) {
    const token = localStorage.getItem('auth:token');
    return this.post('/transactions/client/Transactions/CreateFiatWithdrawal', withdrawalData, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'authorization': `Bearer ${token}` // Interceptor'ı bypass etmek için
      }
    });
  }

  // ===== TRADING METHODS =====

  // Get market URL for trading interface
  async getMarketUrl() {
    const token = localStorage.getItem('auth:token');
    return this.get('/trader/client/Trader/GetTraderUrl', {
      headers: {
        'Authorization': `Bearer ${token}`,
        'authorization': `Bearer ${token}`, // Interceptor'ı bypass etmek için
      }
    });
  }

  // ===== DOCUMENT UPLOAD METHODS =====

  // Upload individual document
  async uploadDocument(formData, documentType) {
    const token = localStorage.getItem('auth:token');

    // Different endpoints for different document types
    const endpoints = {
      'idFront': '/v1/Sca/Documents/Upload/identity',
      'idBack': '/v1/Sca/Documents/Upload/bank',
      'addressProof': '/v1/Sca/Documents/Upload/addressproof'
    };

    const endpoint = endpoints[documentType];
    if (!endpoint) {
      throw new Error('Invalid document type');
    }

    return this.post(endpoint, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        'Authorization': `Bearer ${token}`
      }
    }, 'default');
  }

  // Get existing documents
  async getExistingDocuments() {
    const token = localStorage.getItem('auth:token');
    return this.get('/v1/Sca/Documents/MyDocuments', {
      headers: {
        'Authorization': `Bearer ${token}`,
        'authorization': `Bearer ${token}`,
        'Accept': 'application/json, text/plain, */*'
      }
    });
  }

  // ===== GLOBAL APPS =====
  async getApps() {
    return this.get('/Global/Apps', {
      headers: {
        'Accept': 'application/json, text/plain, */*'
      }
    }, 'default');
  }

  // ===== PASSWORD RECOVERY =====
  async sendForgotPasswordEmail(email) {
    const endpoint = `/authentication/Auth/ForgotPassword?email=${encodeURIComponent(email)}`;
    return this.post(endpoint, null, {
      headers: {
        'Accept': 'application/json, text/plain, */*'
      }
    }, 'default');
  }

  // ===== NEWS & CONTENT =====
  async getAllNews() {
    return this.get('/cms/client/Content/GetAllNews', {
      headers: {
        'Accept': 'application/json, text/plain, */*'
      }
    }, 'default');
  }

  async getAllAnnouncements() {
    return this.get('/cms/client/Content/GetAllAnnouncements', {
      headers: {
        'Accept': 'application/json, text/plain, */*'
      }
    }, 'default');
  }

  async getNewsById(newsId) {
    return this.get(`/cms/client/Content/GetNewsById/${newsId}`, {
      headers: {
        'Accept': 'application/json, text/plain, */*'
      }
    }, 'default');
  }

  async getAnnouncementById(announcementId) {
    return this.get(`/cms/client/Content/GetAnnouncementById/${announcementId}`, {
      headers: {
        'Accept': 'application/json, text/plain, */*'
      }
    }, 'default');
  }

  async getAllPromotions() {
    return this.get('/cms/client/Content/GetAllPromotions', {
      headers: {
        'Accept': 'application/json, text/plain, */*'
      }
    }, 'default');
  }

  async getPromotionById(promotionId) {
    return this.get(`/cms/client/Content/GetPromotionById/${promotionId}`, {
      headers: {
        'Accept': 'application/json, text/plain, */*'
      }
    }, 'default');
  }

  // ===== RSS FEED =====
  async getRSSFeed() {
    try {
      const xmlText = await this.get('/cms/client/Rss/GetCoinTelegraphRss', {
        responseType: 'text',
        transformResponse: [(data) => data],
        headers: {
          'Accept': 'application/xml, text/xml, application/rss+xml, */*'
        }
      });
      return { success: true, data: xmlText };
    } catch (error) {
      console.error('RSS feed hatası:', error);
      return { success: false, message: error.message };
    }
  }
}

// Singleton instance
const api = new ApiService();

export default api;
